import { validateIban } from "src/helpers/sanitizeInput";
import {
    RegisterFormData,
    PLAN_MAX_DOCTORS,
    PLAN_MAX_STAFF,
    DOCTOR_ROLES,
    STAFF_ROLES,
} from "./registerTypes";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type RegisterTranslate = (key: string, options?: Record<string, string | number>) => string;

export function getRegisterFieldErrors(
    step: number,
    data: RegisterFormData,
    ctx: { praxisIdAvailable: boolean | null; t: RegisterTranslate },
): string[] {
    const { praxisIdAvailable, t } = ctx;
    const errs: string[] = [];

    const PASSWORD_UPPER = /[A-Z]/;
    const PASSWORD_SPECIAL = /[^A-Za-z0-9]/;

    if (step === 1) {
        if (!data.praxisName.trim()) errs.push(t("register.errorPraxisName"));
        if (!EMAIL_RE.test(data.praxisEmail)) errs.push(t("register.errorEmail"));
        if (data.praxisPassword.length < 8) errs.push(t("register.errorPasswordLength"));
        else if (!PASSWORD_UPPER.test(data.praxisPassword) || !PASSWORD_SPECIAL.test(data.praxisPassword))
            errs.push(t("register.errorPasswordStrength"));
        if (data.praxisPassword !== data.praxisConfirmPassword) errs.push(t("register.errorPasswordMatch"));
        if (!data.praxisId.trim()) errs.push(t("register.errorPraxisId"));
        else if (praxisIdAvailable === false) errs.push(t("register.errorIdTaken"));
    }
    if (step === 2) {
        if (!data.specialization.length) errs.push(t("register.errorSpecialization"));
        if (!data.street.trim()) errs.push(t("register.errorStreet"));
        if (!data.plz.trim()) errs.push(t("register.errorPlz"));
        if (!data.city.trim()) errs.push(t("register.errorCity"));
        if (!data.phones.length || !data.phones.some((p) => p.value.trim()))
            errs.push(t("register.errorPhone"));
    }
    if (step === 3) {
        if (!data.iban.trim()) errs.push(t("register.errorIban"));
        else if (!validateIban(data.iban)) errs.push(t("register.errorIbanFormat"));
        if (!data.ibanConsent) errs.push(t("register.errorIbanConsent"));
    }
    if (step === 4) {
        if (!data.plan) errs.push(t("register.errorPlan"));
    }
    if (step === 5) {
        const hasAdmin = data.members.some((m, i) => i === 0 || m.userRights === 3);
        if (!data.members.length || !hasAdmin) errs.push(t("register.errorNoAdmin"));

        const maxDoctors = PLAN_MAX_DOCTORS[data.plan] ?? 1;
        const maxStaff = PLAN_MAX_STAFF[data.plan] ?? 0;
        const doctorCount = data.members.filter((m, i) => i === 0 || DOCTOR_ROLES.has(m.userRights)).length;
        const staffCount = data.members.filter((m, i) => i > 0 && STAFF_ROLES.has(m.userRights)).length;

        if (doctorCount > maxDoctors)
            errs.push(t("register.errorTooManyDoctors", { max: maxDoctors, count: doctorCount }));
        if (staffCount > maxStaff)
            errs.push(t("register.errorTooManyStaff", { max: maxStaff, count: staffCount }));

        data.members.forEach((m, i) => {
            const label = m.userRights === 3 ? t("register.adminBadge") : `${t("register.member")} ${i + 1}`;
            if (!m.firstName.trim()) errs.push(`${label}: ${t("register.errorFirstName")}`);
            if (!m.lastName.trim()) errs.push(`${label}: ${t("register.errorLastName")}`);
            if (!EMAIL_RE.test(m.email)) errs.push(`${label}: ${t("register.errorEmail")}`);
            if (m.password.length < 8) errs.push(`${label}: ${t("register.errorPasswordLength")}`);
            else if (!PASSWORD_UPPER.test(m.password) || !PASSWORD_SPECIAL.test(m.password))
                errs.push(`${label}: ${t("register.errorPasswordStrength")}`);
            if (m.password !== m.confirmPassword) errs.push(`${label}: ${t("register.errorPasswordMatch")}`);
            if ((m.userRights === 3 || m.userRights === 2) && !m.specialty.length)
                errs.push(`${label}: ${t("register.errorSpecialization")}`);
        });
    }
    return errs;
}
